import { useCallback, useRef } from "react";

import { AppointmentEditModel } from "@/Appointments/Models/UI/IAppointmentEditModel";
import { Appointment as AppointmentResponse } from "@/Common/Api/Appointment/Dto/Appointment";
import { Client } from "@/Common/Api/Client";
import { MedicalNotePinOption } from "@/Common/Models/Employee/Enums/MedicalNotePinOption";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";

const useNoteActions = (): ((
    actionType: MedicalNotePinOption,
    appointmentId?: string,
    onApprove?: (onSuccess?: () => void) => Promise<void>
) => void) => {
    const isClosingRef = useRef(false);
    const loadAppointment = useCallback(async (appointmentId?: string): Promise<AppointmentResponse | null> => {
        if (!appointmentId) return null;

        const response = await Client.postAsync<{ Appointment: AppointmentResponse }>(
            ServerOperations.Server.Myle_LoadAppointment,
            { AppointmentId: appointmentId, IncludeReferal: false }
        );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return null;
        }

        return response.data.Appointment;
    }, []);

    const completeNoteAppointment = useCallback(async (appointment?: AppointmentResponse): Promise<void> => {
        if (!appointment) return;

        const completedStateLookup = cacheStore.GetSingleAppointmentStateLookup("FINISHED").firstOrDefault();
        const completedAppointmentData = {
            ...appointment,
            Notes: "",
            AppointmentStateLookupId: completedStateLookup.LookupID,
            AppointmentStateLookupCode: completedStateLookup.LookupCode,
            AppointmentStateValue: cacheStore.GetLocalizedValueByLookupId(completedStateLookup.LookupID)
        };

        return sessionStore.dispatch(LocalPatientOperations.MedicalNote.CompleteMedicalNoteLinkedAppointment, { appointment: completedAppointmentData });
    }, []);

    const onNoteActionClick = useCallback((actionType: MedicalNotePinOption, appointmentId?: string, onApprove?: (onSuccess?: () => void) => Promise<void>) => {
        switch (actionType) {
            case MedicalNotePinOption.Save:
                sessionStore.dispatch(LocalPatientOperations.MedicalNote.SaveEditNote);
                break;

            case MedicalNotePinOption.SaveClose:
                sessionStore.dispatch(LocalPatientOperations.MedicalNote.SaveEditNote, {
                    then: () => sessionStore.dispatch(LocalOperations.Person.CloseFile)
                });
                break;

            case MedicalNotePinOption.SaveComplete:
                sessionStore.dispatch(LocalPatientOperations.MedicalNote.SaveEditNote, {
                    then: async () => {
                        if (!appointmentId) return;

                        const appointment = await loadAppointment(appointmentId);
                        const isAppointmentCompleted = AppointmentEditModel.isAppointmentCompleted(appointment?.AppointmentStateLookupCode);

                        if (isAppointmentCompleted || !appointment) return;

                        completeNoteAppointment(appointment);
                    }
                });
                break;

            case MedicalNotePinOption.SaveCompleteClose:
                sessionStore.dispatch(LocalPatientOperations.MedicalNote.SaveEditNote, {
                    then: async () => {
                        const appointment = await loadAppointment(appointmentId);
                        const isAppointmentCompleted = AppointmentEditModel.isAppointmentCompleted(appointment?.AppointmentStateLookupCode);

                        if (isAppointmentCompleted || !appointment) {
                            return sessionStore.dispatch(LocalOperations.Person.CloseFile);
                        }

                        completeNoteAppointment(appointment).then(() => sessionStore.dispatch(LocalOperations.Person.CloseFile));
                    }
                });
                break;

            case MedicalNotePinOption.Approve:
                onApprove?.();
                break;

            case MedicalNotePinOption.ApproveClose:
                if (isClosingRef.current) return;
                isClosingRef.current = true;
                onApprove?.(() => sessionStore.dispatch(LocalOperations.Person.CloseFile));
                break;

            case MedicalNotePinOption.ApproveComplete:
                (async () => {
                    const appointment = await loadAppointment(appointmentId);
                    const isAppointmentCompleted = AppointmentEditModel.isAppointmentCompleted(appointment?.AppointmentStateLookupCode);

                    if (isAppointmentCompleted || !appointment) {
                        return onApprove?.();
                    }

                    await completeNoteAppointment(appointment);
                    onApprove?.();
                })();
                break;

            case MedicalNotePinOption.ApproveCompleteClose:
                if (isClosingRef.current) return;
                isClosingRef.current = true;
                (async () => {
                    const appointment = await loadAppointment(appointmentId);
                    const isAppointmentCompleted = AppointmentEditModel.isAppointmentCompleted(appointment?.AppointmentStateLookupCode);

                    if (appointment && !isAppointmentCompleted) {
                        await completeNoteAppointment(appointment);
                    }

                    onApprove?.(() => sessionStore.dispatch(LocalOperations.Person.CloseFile));
                })();
                break;
        }

        sessionStore.dispatch(LocalPatientOperations.MedicalNote.SaveNoteActionButtonLog, { actionType, appointmentId });
    }, []);

    return onNoteActionClick;
};

export { useNoteActions };
